/**
 * SvelteKit Client-Side Error Handler
 *
 * This hook catches ALL unhandled errors in the SPA and sends them to PostHog.
 *
 * Captures:
 * - Svelte component errors
 * - Route loading errors
 * - Unhandled promise rejections
 * - Any error that bubbles up without a try-catch
 *
 * @see https://kit.svelte.dev/docs/hooks#shared-hooks-handleerror
 */

import posthog from 'posthog-js';
import { initFaro } from '$lib/utils/faro';

// ============ CUSTOM MODIFICATION: Polyfills for Chrome < 98 / Safari < 15.4 ============
// Fixes: "structuredClone is not defined" (Chrome < 98) and
//        "n.findLast is not a function" (@tiptap/core on Chrome < 97).
// These polyfills must execute before any component or library code that uses these APIs.
// See issue #451.
if (!Array.prototype.findLast) {
	// eslint-disable-next-line no-extend-native
	Object.defineProperty(Array.prototype, 'findLast', {
		value: function (predicate, thisArg) {
			for (let i = this.length - 1; i >= 0; i--) {
				if (i in this && predicate.call(thisArg, this[i], i, this)) {
					return this[i];
				}
			}
			return undefined;
		},
		writable: true,
		configurable: true,
		enumerable: false
	});
}

if (!Array.prototype.findLastIndex) {
	// eslint-disable-next-line no-extend-native
	Object.defineProperty(Array.prototype, 'findLastIndex', {
		value: function (predicate, thisArg) {
			for (let i = this.length - 1; i >= 0; i--) {
				if (i in this && predicate.call(thisArg, this[i], i, this)) {
					return i;
				}
			}
			return -1;
		},
		writable: true,
		configurable: true,
		enumerable: false
	});
}

// Note: JSON.parse/JSON.stringify cannot handle functions, Dates (become strings),
// Maps/Sets, RegExps, undefined, or circular refs — but the app only calls
// structuredClone on plain JSON-serialisable data, so this is sufficient.
if (typeof window.structuredClone === 'undefined') {
	window.structuredClone = (value) => JSON.parse(JSON.stringify(value));
}
// ============ END CUSTOM MODIFICATION ============

// ============ CUSTOM MODIFICATION: Faro benign error filtering ============
// Suppress benign / non-actionable errors BEFORE Faro registers its listeners.
//
// Faro's getWebInstrumentations() attaches global `error` and
// `unhandledrejection` handlers. By installing capture-phase listeners first
// we can preventDefault + stopImmediatePropagation to prevent Faro (and
// Grafana Cloud alerts) from ever seeing these patterns.
//
// Related issues: #206, #207, #209, #211, #213, #215, #216, #217, #220, #221,
//                 #222, #223, #230, #231, #234, #235, #238, #244, #245, #247,
//                 #248, #267, #270, #273
// ---------------------------------------------------------------------------
if (typeof window !== 'undefined') {
	/**
	 * Error message substrings that are safe to suppress.
	 * Each entry should be a short, unique fragment — we match with `includes()`.
	 */
	const BENIGN_ERROR_PATTERNS = [
		// Cross-origin script errors — browser strips all details (not actionable)
		'Script error.',

		// Benign layout observer warnings — spec-defined, no user impact
		// See: https://www.w3.org/TR/resize-observer/#deliver-resize-loop-error
		'ResizeObserver loop completed with undelivered notifications',
		'ResizeObserver loop limit exceeded',

		// CefSharp bot crawlers (Outlook SafeSearch, security scanners, etc.)
		'Object Not Found Matching Id:',

		// Permission denials — user denied or insecure context (#216). Intentionally
		// broad: covers Clipboard, getUserMedia, .play(), Fullscreen, Notifications,
		// and WakeLock APIs. These are all user/environment permission denials, not
		// application bugs. If a specific NotAllowedError needs investigation, check
		// Faro sessions with this filter temporarily removed.
		'NotAllowedError',

		// Dynamic import / chunk load failures — stale client bundles after deploy (#207).
		// Safari reports "Load failed", Chrome/Firefox/Safari have distinct messages
		// for dynamic import() failures. These are transient and self-heal on reload.
		'Load failed',
		'Failed to fetch dynamically imported module',
		'error loading dynamically imported module',
		'Importing a module script failed',

		// Vite CSS preload failures — stale CDN assets after deployment (#223)
		'Unable to preload CSS',

		// BroadcastChannel unavailable on Safari < 15.4 / restricted contexts (#221)
		"Can't find variable: BroadcastChannel",

		// ProseMirror/TipTap stale document position errors — transient editor state (#222).
		// Format is "Position N out of range" from prosemirror-model Node.resolve().
		// Using "out of range" as the match — specific enough for ProseMirror while
		// catching all variants (the "Position" prefix varies by ProseMirror version).
		'out of range',

		// Null config race: getBackendConfig() returns null on transient backend errors.
		// +layout.svelte guards this with optional chaining, but keep as safety net (#214).
		"reading 'default_locale'",
		'r.default_locale',

		// Null/undefined message array properties (childrenIds, files, sources, citations, models)
		// in chat history loaded from automation-produced or corrupted data (#244, #273).
		'is not iterable',

		// Transient network failures — mobile networks, brief connectivity drops (#215).
		// Chrome/Firefox throw "Failed to fetch"; Safari throws "Load failed" (covered above).
		'Failed to fetch',

		// Svelte framework media element race condition (#213) — controlsList property
		// set after element destruction. Not in our code; this is a Svelte internal.
		"setting 'controlsList'",

		// Safari-specific SyntaxError from Response.json() on non-JSON body or
		// URL/regex parsing differences in WebKit (#231).
		'The string did not match the expected pattern',

		// ProseMirror top-level node boundary error — transient editor state (#245).
		// Thrown by ResolvedPos.before(0) when cursor is at document root.
		'no position before',

		// AbortError from overlapping fullscreen operations — e.g. requestFullscreen()
		// cancellation surfaces as "Pending operation cancelled" (#248). Use the
		// specific message substring instead of the broad "AbortError" name so
		// that other AbortError sources (e.g. fetch() cancellations) are not
		// globally suppressed.
		'Pending operation cancelled',

		// SecurityError from localStorage/sessionStorage access in restricted browser
		// contexts: cross-origin iframes, certain privacy modes, enterprise browsers (#234).
		// Match on specific storage property substrings to avoid suppressing all SecurityErrors.
		"localStorage' property",
		"sessionStorage' property",

		// Backend 404 responses surfacing as unhandled promise rejections (#270).
		"We could not find what you're looking for",

		// Expected auth rejections — session expiry / concurrent login surface as a
		// bare "401 Unauthorized" rejection from API helpers that don't catch. The
		// app re-routes to login on its own; this is not an actionable code bug, so
		// keep it out of Faro telemetry.
		'401 Unauthorized',

		// Transient Cloudflare origin errors (520/521/522) — the origin was briefly
		// overloaded and self-heals on retry. Not an application bug.
		'invalid or incomplete response to Cloudflare'
	];

	/**
	 * Extract a matchable string from any value.
	 * Promise rejections can carry strings, Error objects, or arbitrary values.
	 *
	 * BUG FIX (#216): DOMException extends Error, but stores the error type in
	 * `.name` (e.g. "NotAllowedError", "SecurityError"), not in `.message`.
	 * The original code only returned `.message`, so pattern matching against
	 * error names (like "NotAllowedError") never matched DOMExceptions.
	 * Fix: always include both `.name` and `.message` for Error instances.
	 */
	function extractMessage(value) {
		if (typeof value === 'string') return value;
		if (value instanceof Error) return `${value.name}: ${value.message}`;
		if (value?.message) return String(value.message);
		return String(value ?? '');
	}

	function isBenign(message) {
		return BENIGN_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
	}

	// --- error events (regular JS errors) ---
	window.addEventListener(
		'error',
		(event) => {
			if (isBenign(event.message ?? '')) {
				event.stopImmediatePropagation();
				event.preventDefault();
			}
		},
		true // capture phase — runs before Faro's bubble-phase listener
	);

	// --- unhandledrejection events (promise rejections, e.g. CefSharp) ---
	window.addEventListener(
		'unhandledrejection',
		(event) => {
			const reason = event.reason;

			// Suppress plain-object rejections — typically API error responses thrown
			// as {code, message} or {detail} instead of proper Error instances (#230).
			// These are caught and displayed by the UI; the unhandled rejection is a
			// leak from callers that don't await/catch the API promise.
			if (
				reason &&
				typeof reason === 'object' &&
				!(reason instanceof Error) &&
				('code' in reason || 'detail' in reason)
			) {
				event.stopImmediatePropagation();
				event.preventDefault();
				return;
			}

			if (isBenign(extractMessage(reason))) {
				event.stopImmediatePropagation();
				event.preventDefault();
			}
		},
		true
	);
}
// ============ END CUSTOM MODIFICATION ============

// Initialize Grafana Faro for frontend observability, gated on backend config.
// Only initializes when the backend has ENABLE_FARO=true and returns a collector URL.
// Enterprise deployments with ENABLE_FARO=false will never send telemetry.
const initializeFaroFromBackendConfig = async () => {
	if (typeof window === 'undefined') {
		return;
	}

	try {
		const response = await fetch('/api/config');

		if (!response.ok) {
			return;
		}

		const backendConfig = await response.json();

		if (backendConfig?.faro?.enabled && backendConfig.faro.collector_url) {
			initFaro(backendConfig.faro.collector_url);
		}
	} catch (error) {
		console.error('[Faro] Failed to load backend config:', error);
	}
};

void initializeFaroFromBackendConfig();

/**
 * Global error handler for all client-side errors
 * @param {Object} params
 * @param {Error} params.error - The error that was thrown
 * @param {number} params.status - HTTP status code (if applicable)
 * @returns {Object} Error object to display to user
 */
export const handleError = ({ error, status }) => {
	// Don't capture 404s (not really errors, just missing routes)
	if (status !== 404) {
		// Send to PostHog
		try {
			posthog.captureException(error, {
				tags: {
					status: status,
					type: 'svelte_client_error'
				}
			});
		} catch (posthogError) {
			console.error('[PostHog] Failed to capture exception:', posthogError);
		}
	}

	// Always log to console for development
	console.error('[Client Error]', error);

	// Return user-friendly error message
	// This is what SvelteKit will display on the error page
	return {
		message: error?.message || 'An unexpected error occurred'
	};
};
